import type { ComprehensionCallback } from './comprehension/base';
import type { DocAiDocument } from '@/types/api-response/doc-ai-types';
import type { DocumentSort } from '@/types/measure-document-types';
import type { UrlSerializableFilterState } from '@/types/nmed/nmed-filter-types';
import type { FilterState, SearchKeyItem, SortDirection, SortOrder } from '@/types/nmed/shared-filter-types';
import { defineStore, storeToRefs } from 'pinia';
import { computed, nextTick, reactive, ref, watch } from 'vue';
import { createMeasureDocumentAdapter } from '@/adapters/measure-document-adapter';
import { useDocumentDetailsFetch, useDocumentFilterState, useDocumentMetadata, useDocumentPagination } from '@/composables/nmed/shared';
import { EXPORT_LIMIT } from '@/composables/nmed/use-document-export';
import { useNmedDocSearchUndoHistory } from '@/composables/nmed/use-nmed-doc-search-undo-history';
import { useDocumentSchema } from '@/composables/use-document-schema';
import { fetchDocumentsByMeasureIds } from '@/services/document-client';
import { useMapUiStore } from '@/stores/v2/map-ui-store';
import { removeItemFromArray } from '@/utils/array-utils';
import { filterStateToDocumentFilters } from '@/utils/filter-state-to-document-filters';
import { areFiltersEqual } from '@/utils/filter-url-utils';
import { isKnownSchemaField, toSortField } from '@/utils/schema-field-key';
import { looksLikeFilename, parseUserQuery } from '@/utils/search-query-parser';
import { resolveSearchQuery } from '@/utils/search-query-resolver';
import { humanizeFieldName } from '@/utils/string-utils';
import { comprehendDischargePermitPattern, comprehendDischargePermits } from './comprehension/discharge-permits';
import { comprehendGCP, comprehendModificationPermit, comprehendNOI, comprehendNSR, comprehendPSD } from './comprehension/permit-numbers';

/* ---------------------------------------------------------------------------------------------- */
/*                                           TYPES                                                */
/* ---------------------------------------------------------------------------------------------- */

/**
 * Represents the state of search keyword filtering
 * Uses a grouped structure where each field maps to an array of values
 */
export interface SearchKeywordState {
   /** Grouped filters: { 'County': ['Bernalillo', 'Santa Fe'], 'FacilityName': ['Plant A'] } */
   filters: Record<string, string[]>;
}

/**
 * Represents the sort order for document results
 */
export { type SortOrder } from '@/types/nmed/shared-filter-types';

/**
 * Represents the sort direction for table columns
 */
export { type SortDirection } from '@/types/nmed/shared-filter-types';

/**
 * Sort configuration for table-based document search
 */
export { type SortConfig } from '@/types/nmed/shared-filter-types';

/**
 * Doc-search specific filter state (fields not in sharedFilterState)
 */
export interface DocSearchLocalState {
   deepSearchKeyword: string;
}

/* ---------------------------------------------------------------------------------------------- */
/*                                        CONSTANTS                                               */
/* ---------------------------------------------------------------------------------------------- */

const DEFAULT_ROWS_PER_PAGE = 30;

/** Server-side cap on `per_page` for the document search endpoint. */
const MAX_SEARCH_PAGE_SIZE = 500;

const PAGE_SIZE_STORAGE_KEY = 'doc-search-page-size';
const FILTER_STATE_STORAGE_KEY = 'doc-search-filter-state';

/**
 * Get persisted page size from localStorage, or return default
 */
function getPersistedPageSize(): number {
   try {
      const stored = localStorage.getItem(PAGE_SIZE_STORAGE_KEY);
      if (stored) {
         const parsed = Number.parseInt(stored, 10);
         // Validate it's a reasonable page size
         if ([25, 50, 100, 200].includes(parsed)) {
            return parsed;
         }
      }
   }
   catch {
      // localStorage not available
   }
   return DEFAULT_ROWS_PER_PAGE;
}

/**
 * Persist page size to localStorage
 */
function persistPageSize(size: number): void {
   try {
      localStorage.setItem(PAGE_SIZE_STORAGE_KEY, String(size));
   }
   catch {
      // localStorage not available
   }
}

/**
 * Persist filter state to sessionStorage for navigation recovery
 */
function persistFilterState(state: UrlSerializableFilterState): void {
   try {
      sessionStorage.setItem(FILTER_STATE_STORAGE_KEY, JSON.stringify(state));
   }
   catch {
      // sessionStorage not available
   }
}

/**
 * Get persisted filter state from sessionStorage
 * Validates the parsed object shape before returning
 */
function getPersistedFilterState(): UrlSerializableFilterState | null {
   try {
      const stored = sessionStorage.getItem(FILTER_STATE_STORAGE_KEY);
      if (stored) {
         const parsed = JSON.parse(stored);

         // Validate required fields exist and have correct types
         if (
            typeof parsed !== 'object'
            || parsed === null
            || !Array.isArray(parsed.bureaus)
            || !Array.isArray(parsed.documentTypes)
            || typeof parsed.searchFilters !== 'object'
            || parsed.searchFilters === null
         ) {
            return null;
         }

         return parsed as UrlSerializableFilterState;
      }
   }
   catch {
      // sessionStorage not available or invalid JSON
   }
   return null;
}

/**
 * Clear persisted filter state from sessionStorage
 */
function clearPersistedFilterState(): void {
   try {
      sessionStorage.removeItem(FILTER_STATE_STORAGE_KEY);
   }
   catch {
      // sessionStorage not available
   }
}

/**
 * Pinia store for managing NMED document search filters and state
 */
export const useNmedDocSearchFilterStore = defineStore('docSearchFilter', () => {
   const mapUiStore = useMapUiStore();
   const { markersLoadingState } = storeToRefs(mapUiStore);
   const documentAdapter = createMeasureDocumentAdapter();

   /* ------------------------------------------------------------------------------------------- */
   /*                              SHARED COMPOSABLES                                             */
   /* ------------------------------------------------------------------------------------------- */

   // Filter state (multi-select mode)
   const {
      filterState: sharedFilterState,
      isFilterApplied: sharedIsFilterApplied,
      setSearchFilters: setSharedSearchFilters,
      toggleSearchFilterValue: sharedToggleSearchFilterValue,
      setGlobalSearchKeyword: setSharedGlobalSearchKeyword,
      clearAllFilters: clearSharedFilterState,
      clearSearchKeywords: sharedClearSearchKeywords,
   } = useDocumentFilterState();

   // Pagination & Sort (use persisted page size if available)
   const initialPageSize = getPersistedPageSize();
   const {
      pagination: sharedPagination,
      sortConfig,
      $reset: resetPaginationState,
   } = useDocumentPagination({ itemsPerPage: initialPageSize });

   // Document fetching — the same request the saved-documents and projects
   // surfaces make, minus their project scope.
   const {
      fetchState: fetchDocumentsState,
      availableFields: availableFieldsFromResponse,
      availableBureaus: bureausInResults,
      availableDocTypes: docTypesInResults,
      fetchDocuments: fetchDocumentPage,
      abortRequest,
      $reset: resetFetchState,
   } = useDocumentDetailsFetch({ adapter: documentAdapter });

   // The page renders a spinner until the first result set lands; the shared
   // fetch state starts idle because most surfaces mount without fetching.
   fetchDocumentsState.isLoading = true;

   // Every filterable field, independent of what the current page happens to
   // carry — see `useDocumentSchema`.
   const { schemaFieldKeys, loadSchema } = useDocumentSchema();
   loadSchema();

   /* ------------------------------------------------------------------------------------------- */
   /*                                         STATE                                               */
   /* ------------------------------------------------------------------------------------------- */

   /**
    * Tracks whether Luma AI response documents have been added to the search results
    */
   const isLumaDocsAdded = ref<boolean>(false);

   /**
    * Chat ID associated with Luma AI document results
    */
   const lumaDocsChatId = ref<string>('');

   /**
    * Thread the currently loaded Luma references were cited by.
    *
    * Kept beside the message id because the message alone cannot be joined to
    * the question that produced it. This is what lets an open that followed a
    * citation be traced back to the ask, and on to a project.
    */
   const lumaDocsThreadId = ref<number | undefined>(undefined);

   /**
    * Generation counter for `addLumaDocs`, so a slower earlier load cannot land
    * its documents against a newer answer's thread.
    */
   let lumaDocsRequestId = 0;

   /**
    * Leave Luma mode, and disown any Luma load still in flight.
    *
    * The measure-ids fetch is called directly rather than through the shared
    * adapter, so `abortRequest` does not reach it. Bumping the generation is what
    * stops a pending load resolving over an ordinary search and restoring the
    * documents, paging and citation thread the user just moved away from.
    */
   function clearLumaDocs() {
      lumaDocsRequestId += 1;
      isLumaDocsAdded.value = false;
      lumaDocsChatId.value = '';
      lumaDocsThreadId.value = undefined;
   }

   /**
    * Doc-search specific filter state (fields not shared with other stores)
    */
   const docSearchLocalState = reactive<DocSearchLocalState>({
      deepSearchKeyword: '',
   });

   /**
    * Counter used to trigger force-clear of search keywords
    */
   const searchKeywordsForceClearCount = ref<number>(0);

   /**
    * Doc-search specific pagination state for page-based navigation
    */
   const localPaginationState = reactive({
      firstRow: 0,
      numberOfRows: initialPageSize,
   });

   /**
    * Tracks whether the store has been initialized
    */
   const isInitialized = ref<boolean>(false);

   /**
    * Undo history for filter states
    */
   const undoHistory = useNmedDocSearchUndoHistory();

   /**
    * Track the last serializable state to avoid duplicate pushes
    */
   let lastSerializedState: UrlSerializableFilterState | null = null;

   // Metadata (derived from search response data)
   const {
      bureaus,
      documentTypes,
      searchFields,
      hasNoFilterOptions,
   } = useDocumentMetadata({
      responseBureaus: bureausInResults,
      responseDocTypes: docTypesInResults,
      responseSearchFields: availableFieldsFromResponse,
   });

   /* ------------------------------------------------------------------------------------------- */
   /*                                   COMPUTED - FILTER STATUS                                  */
   /* ------------------------------------------------------------------------------------------- */

   /**
    * Checks if any filter is currently applied
    */
   const isFilterApplied = computed(() => {
      return (
         sharedIsFilterApplied.value
         || docSearchLocalState.deepSearchKeyword !== ''
      );
   });

   /**
    * Checks if deep search (AI/semantic search) filter is active
    */
   const isDeepSearchFilterApplied = computed(() => {
      return docSearchLocalState.deepSearchKeyword !== '';
   });

   /**
    * Checks if global search (across all metadata) filter is active
    */
   const isGlobalSearchApplied = computed(() => {
      return sharedFilterState.globalSearchKeyword !== '';
   });

   /**
    * Checks if field-specific search keywords are applied
    */
   const isSearchKeywordApplied = computed(() => {
      return Object.keys(sharedFilterState.searchKeyword.filters).length > 0;
   });

   /**
    * Available bureaus from the search response.
    * Narrowing now happens server-side via the search response.
    */
   const relevantBureaus = computed(() => bureaus.value);

   /**
    * Detects filters that are no longer valid for the current selection.
    *
    * Bureaus and doc types are checked against the facets the last search
    * returned. Field filters are checked against the *schema* instead: a field
    * the schema doesn't know cannot be translated into a request at all, so it
    * is silently dropped from the query rather than narrowing it. That is
    * exactly how a filter saved under the old raw NMED column names
    * (`PermitNumber`) shows up, and the user needs to see it rather than trust
    * a result set their visible filters don't describe.
    */
   const staleFilters = computed(() => {
      const staleDocTypes = sharedFilterState.documentTypes.filter(
         dt => documentTypes.value.length > 0 && !documentTypes.value.includes(dt),
      );
      const staleSearchFields = Object.keys(sharedFilterState.searchKeyword.filters).filter(
         field => !isKnownSchemaField(field, schemaFieldKeys.value),
      );
      const staleBureaus = sharedFilterState.bureaus.filter(
         b => relevantBureaus.value.length > 0 && !relevantBureaus.value.includes(b),
      );
      return { bureaus: staleBureaus, documentTypes: staleDocTypes, searchFields: staleSearchFields };
   });

   /**
    * Display label for a field key.
    *
    * No endpoint returns labels — the schema gives group and code, and the code
    * humanizes directly, so there is one humanizer rather than a server-supplied
    * map that can drift from it.
    */
   function getFieldDisplayName(rawFieldKey: string): string {
      return humanizeFieldName(rawFieldKey);
   }

   /* ------------------------------------------------------------------------------------------- */
   /*                                        WATCHERS                                             */
   /* ------------------------------------------------------------------------------------------- */

   /**
    * Drops a sort the API cannot express.
    *
    * This used to clear the sort whenever the field left the result set's field
    * list, which the legacy response supplied. Absence from a page is no longer
    * a reason to drop it — the search index sorts on fields this page doesn't
    * happen to populate, putting the missing ones last. What genuinely cannot be
    * sent is a key that doesn't translate, so that is what gets cleared, and the
    * table's own sort indicator is cleared with it.
    */
   watch(() => sharedFilterState.sortField, (field) => {
      if (field && toSortField(field) === null) {
         sharedFilterState.sortField = '';
         sharedFilterState.sortOrder = '';
         sortConfig.field = '';
         sortConfig.direction = 'ASC';
      }
   }, { immediate: true });

   /**
    * Watches filter state and persists to sessionStorage for navigation recovery.
    * Only persists when there are actual filters applied (not empty state).
    * This prevents Luma operations on the chat page from overwriting saved filters.
    */
   watch(
      () => ({
         bureaus: [...sharedFilterState.bureaus],
         documentTypes: [...sharedFilterState.documentTypes],
         searchFilters: { ...sharedFilterState.searchKeyword.filters },
         globalSearchKeyword: sharedFilterState.globalSearchKeyword,
         sortField: sharedFilterState.sortField,
         sortOrder: sharedFilterState.sortOrder,
      }),
      (newState) => {
         // Only persist if initialized and there are actual filters to save.
         // This prevents Luma/chat operations from overwriting saved filter state.
         const hasFilters = newState.bureaus.length > 0
            || newState.documentTypes.length > 0
            || Object.keys(newState.searchFilters).length > 0
            || newState.globalSearchKeyword
            || newState.sortField;

         if (isInitialized.value) {
            if (hasFilters) {
               persistFilterState(getSerializableState());
            }
            else {
               // Clear persisted state when all filters are removed
               clearPersistedFilterState();
            }
         }
      },
      { deep: true },
   );

   /* ------------------------------------------------------------------------------------------- */
   /*                                      API FUNCTIONS                                          */
   /* ------------------------------------------------------------------------------------------- */

   /** Sort clause for the current filter state, or undefined for relevancy order. */
   function currentSort(): DocumentSort | undefined {
      const field = toSortField(sharedFilterState.sortField);
      if (!field)
         return undefined;
      return {
         field,
         direction: sharedFilterState.sortOrder === 'asc' ? 'asc' : 'desc',
      };
   }

   /**
    * Fetches documents based on currently applied filters
    */
   async function fetchDocuments() {
      // Clear Luma docs state when fetching new documents
      clearLumaDocs();

      // Add permit markers to loading state
      if (!markersLoadingState.value.includes('permit')) {
         markersLoadingState.value.push('permit');
      }

      const page = sharedPagination.currentPage;
      const result = await fetchDocumentPage({
         filterState: sharedFilterState,
         page,
         perPage: sharedPagination.itemsPerPage,
         sortField: sharedFilterState.sortField,
         sortOrder: sharedFilterState.sortOrder,
      });

      if (result === null) {
         // Either the request failed or a newer one superseded it. A failure
         // should stop the map's spinner; a superseded request must leave it
         // alone, because the request that replaced it is still running and owns
         // the marker now. `error` separates them — a superseding request clears
         // it on the way in, before this one returns.
         if (fetchDocumentsState.error) {
            removeItemFromArray(markersLoadingState.value, 'permit');
         }
         return;
      }

      removeItemFromArray(markersLoadingState.value, 'permit');

      sharedPagination.totalCount = result.total;
      sharedPagination.filteredCount = result.total;
      sharedPagination.totalPages = Math.ceil(result.total / sharedPagination.itemsPerPage);

      // Kept in step with the page for the callers that still reset the offset.
      localPaginationState.firstRow = (page - 1) * sharedPagination.itemsPerPage;
      localPaginationState.numberOfRows = sharedPagination.itemsPerPage;
   }

   /**
    * Fetches all matching document IDs for the current filter state (up to a given limit).
    * Used for "Export All" ZIP when results span multiple pages.
    */
   async function fetchAllMatchingDocIds(limit = EXPORT_LIMIT): Promise<string[]> {
      const docIds: string[] = [];

      try {
         const filters = filterStateToDocumentFilters(sharedFilterState);
         const sort = currentSort();

         // The export allows more documents than one search page may return, so
         // this pages rather than trusting a single oversized request — the
         // server caps `per_page`, and the old code's single call silently
         // exported only the first slice.
         for (let page = 1; docIds.length < limit; page++) {
            const response = await documentAdapter.search({
               keyword: sharedFilterState.globalSearchKeyword,
               filters,
               sort,
               page,
               per_page: Math.min(limit - docIds.length, MAX_SEARCH_PAGE_SIZE),
            });

            if (response.data.length === 0)
               break;

            docIds.push(
               ...response.data
                  .map(result => result.source.result_text ?? '')
                  .filter(Boolean),
            );

            if (docIds.length >= response.meta.total)
               break;
         }

         return docIds.slice(0, limit);
      }
      catch (e) {
         console.error('Failed to fetch all matching doc IDs:', e);
         return [];
      }
   }

   /* ------------------------------------------------------------------------------------------- */
   /*                                    FILTER SETTERS                                           */
   /* ------------------------------------------------------------------------------------------- */

   function setBureaus(bureaus: string[]) {
      sharedFilterState.bureaus = bureaus;
   }

   function setDocumentTypes(documentTypes: string[]) {
      sharedFilterState.documentTypes = documentTypes;
   }

   function setSearchFilters(filters: Record<string, SearchKeyItem[]>) {
      setSharedSearchFilters(filters);
   }

   function toggleSearchFilterValue(field: string, value: string, mode: 'ADD' | 'REMOVE') {
      sharedToggleSearchFilterValue(field, value, mode);
   }

   function setGlobalSearchKeyword(keyword: string) {
      setSharedGlobalSearchKeyword(keyword);
   }

   function setDeepSearchKeyword(keyword: string) {
      docSearchLocalState.deepSearchKeyword = keyword;
   }

   async function addLumaDocs(chatId: string, docs: Record<string, any>[], threadId?: number) {
      abortRequest();

      softReset();

      // `abortRequest` only bumps the generation the shared adapter reads, and
      // the measure-ids fetch below is called directly rather than through it.
      // Two answers landing references in quick succession therefore both run to
      // completion, and the slower one would otherwise write its documents over
      // the newer thread's, leaving every open from the panel labelled with a
      // thread that did not cite it.
      const requestId = ++lumaDocsRequestId;
      const isCurrent = () => requestId === lumaDocsRequestId;

      isLumaDocsAdded.value = true;
      lumaDocsChatId.value = chatId;
      lumaDocsThreadId.value = threadId;

      // Luma references carry `measure_id` alongside the docid; the measure id is
      // what the document API is keyed by. A reference without one can't be
      // resolved to a full document, so it is rendered from the reference itself
      // rather than guessed at.
      const refMeasureId = (ref: Record<string, any>): number => {
         const id = Number(ref.measure_id);
         return Number.isFinite(id) && id > 0 ? id : 0;
      };

      // `DocAiDocument.id` means `Measure.id` everywhere downstream — it is what
      // the document-link endpoint is keyed by. A raw reference's own `id` is the
      // search index's row id, so it is overwritten rather than allowed to pass
      // for a measure id and resolve some unrelated document.
      const asPartialDoc = (ref: Record<string, any>): DocAiDocument =>
         ({ ...ref, id: refMeasureId(ref) }) as DocAiDocument;

      const measureIds = [...new Set(docs.map(refMeasureId).filter(id => id > 0))];

      if (measureIds.length > 0) {
         fetchDocumentsState.isLoading = true;
         try {
            const fullDocs = await fetchDocumentsByMeasureIds(measureIds);
            // A mixed batch keeps its unresolvable references. They were already
            // shown in the chat answer, so dropping them here would leave the
            // panel listing fewer documents than the text cites.
            const seen = new Set<string>();
            const unresolved = docs
               .filter((d) => {
                  if (refMeasureId(d) > 0)
                     return false;
                  // Several references can point at the same document (different
                  // pages); the fetched half is deduplicated, so this half is too.
                  const key = String(d.document_id ?? d.docid ?? '');
                  if (seen.has(key))
                     return false;
                  seen.add(key);
                  return true;
               })
               .map(asPartialDoc);
            if (isCurrent())
               fetchDocumentsState.documents = [...fullDocs, ...unresolved];
         }
         catch {
            // Fall back to partial refs if fetch fails
            if (isCurrent())
               fetchDocumentsState.documents = docs.map(asPartialDoc);
         }
         finally {
            // The newer call owns the spinner once it has taken over, so a
            // superseded one must not switch it off underneath it.
            if (isCurrent())
               fetchDocumentsState.isLoading = false;
         }
      }
      else {
         fetchDocumentsState.documents = docs.map(asPartialDoc);
      }

      // Everything below derives panel state from the documents, and the call
      // that owns them derives it too. `firstRow` accumulates, so a superseded
      // load running this pass would advance the panel's paging by a batch
      // nobody is looking at.
      if (!isCurrent())
         return;

      // Use the actual loaded document count for pagination — numberOfDocs or
      // reference.length may overcount when multiple references point to the
      // same document (different pages).
      const loadedCount = fetchDocumentsState.documents.length;
      fetchDocumentsState.numberOfDocuments = loadedCount;

      // Update pagination to reflect Luma docs (not the full corpus)
      sharedPagination.totalCount = loadedCount;
      sharedPagination.filteredCount = loadedCount;
      sharedPagination.totalPages = 1;
      sharedPagination.currentPage = 1;

      // Derive available fields from docs (fields with at least one non-null value)
      const internalKeys = new Set(['id']);
      const fields = new Set<string>();
      for (const doc of fetchDocumentsState.documents) {
         for (const [key, val] of Object.entries(doc)) {
            if (val != null && val !== '' && !internalKeys.has(key)) {
               fields.add(key);
            }
         }
      }
      availableFieldsFromResponse.value = [...fields];

      localPaginationState.firstRow = localPaginationState.firstRow + loadedCount;
   }

   function addLumaFilterOptions(filter: Record<string, any>[]) {
      softReset();

      const bureauFilter = filter.find(item => 'Bureau' in item);
      if (bureauFilter) {
         setBureaus([bureauFilter.Bureau]);
      }

      const docTypeFilter = filter.find(item => 'DocType' in item);
      if (docTypeFilter) {
         setDocumentTypes([docTypeFilter.DocType]);
      }

      const excludedKeys = ['Bureau', 'DocType'];
      const filters: Record<string, string[]> = {};

      filter.forEach((item) => {
         const [key, value] = Object.entries(item)[0];
         if (!excludedKeys.includes(key)) {
            if (!filters[key]) {
               filters[key] = [];
            }
            if (!filters[key].includes(value)) {
               filters[key].push(value);
            }
         }
      });

      if (Object.keys(filters).length > 0) {
         setSearchFilters(filters);
      }

      handleUpdateFilterDropdown();
   }

   /* ------------------------------------------------------------------------------------------- */
   /*                                   FILTER HANDLERS                                           */
   /* ------------------------------------------------------------------------------------------- */

   async function clearAllFilters() {
      clearSharedFilterState();
      docSearchLocalState.deepSearchKeyword = '';

      localPaginationState.firstRow = 0;
      sharedPagination.currentPage = 1;

      searchKeywordsForceClearCount.value++;

      // Note: We don't clear persisted filter state here because clearAllFilters
      // is called on navigation. We want to preserve filters for when user returns.

      if (fetchDocumentsState.isLoading) {
         abortRequest();
         removeItemFromArray(markersLoadingState.value, 'permit');
      }

      await fetchDocuments();
      pushCurrentStateToHistory();
   }

   async function handleUpdateFilterDropdown() {
      fetchDocumentsState.numberOfDocuments = 0;
      localPaginationState.firstRow = 0;
      sharedPagination.currentPage = 1;

      await fetchDocuments();
      pushCurrentStateToHistory();
   }

   async function handleSortChange(field: string, order: SortOrder) {
      sharedFilterState.sortField = field;
      sharedFilterState.sortOrder = field ? order : '';

      sortConfig.field = field;
      sortConfig.direction = field ? (order.toUpperCase() as SortDirection) : 'ASC';

      localPaginationState.firstRow = 0;
      sharedPagination.currentPage = 1;
      await fetchDocuments();
      pushCurrentStateToHistory();
   }

   function setSortConfig(field: string, direction: SortDirection) {
      sortConfig.field = field;
      sortConfig.direction = direction;

      sharedFilterState.sortField = field;
      sharedFilterState.sortOrder = field ? (direction.toLowerCase() as SortOrder) : '';
   }

   async function handleGlobalSearchKeyword(keyword: string) {
      // Build metadata fields map from search response data
      const metadataFields: Record<string, string[]> = {};
      if (bureausInResults.value.length) {
         metadataFields.bureau = bureausInResults.value;
      }
      if (docTypesInResults.value.length) {
         metadataFields.doc_type = docTypesInResults.value;
      }

      // Parse and resolve the query against known metadata values
      const parsed = parseUserQuery(keyword);

      // Extract filename-like terms and route them to the filename field filter
      const filenameTerms = parsed.must.filter(looksLikeFilename);
      if (filenameTerms.length > 0) {
         parsed.must = parsed.must.filter(t => !looksLikeFilename(t));
         sharedFilterState.searchKeyword.filters.filename = filenameTerms;
      }

      const resolved = resolveSearchQuery(parsed, metadataFields);

      // Only replace filters for fields that had resolved matches

      if (resolved.matchedFields.bureau) {
         sharedFilterState.bureaus = resolved.matchedFields.bureau;
      }
      if (resolved.matchedFields.doc_type) {
         sharedFilterState.documentTypes = resolved.matchedFields.doc_type;
      }

      // Apply any other resolved fields to search keyword filters
      for (const [field, values] of Object.entries(resolved.matchedFields)) {
         if (field !== 'bureau' && field !== 'doc_type') {
            sharedFilterState.searchKeyword.filters[field] = values;
         }
      }

      // Set globalSearchKeyword to only the remaining unmatched terms
      setGlobalSearchKeyword(resolved.globalSearchKey);

      // Flush reactive updates so the search input syncs with the resolved keyword
      await nextTick();

      const newFilterState = await comprehendQueryChange();
      sharedFilterState.bureaus = newFilterState.bureaus;
      sharedFilterState.documentTypes = newFilterState.documentTypes;
      sharedFilterState.searchKeyword.filters = newFilterState.searchKeyword.filters;
      setGlobalSearchKeyword(newFilterState.globalSearchKeyword);

      localPaginationState.firstRow = 0;
      sharedPagination.currentPage = 1;
      await fetchDocuments();
      pushCurrentStateToHistory();
   }

   async function comprehendQueryChange(): Promise<FilterState> {
      const comprehensions: ComprehensionCallback[] = [comprehendDischargePermits, comprehendDischargePermitPattern, comprehendGCP, comprehendNOI, comprehendPSD, comprehendNSR, comprehendModificationPermit];
      let filterState = sharedFilterState;
      for (const comprehend of comprehensions) {
         filterState = await comprehend(filterState);
      }
      return filterState;
   }

   async function handleSearchFilters(filters: Record<string, SearchKeyItem[]>) {
      const currentFilters = sharedFilterState.searchKeyword.filters;
      if (
         Object.keys(currentFilters).length === 0
         && !sharedFilterState.globalSearchKeyword
         && !docSearchLocalState.deepSearchKeyword
         && Object.keys(filters).length === 0
      ) {
         return;
      }

      if (
         JSON.stringify(currentFilters) === JSON.stringify(filters)
         && !(sharedFilterState.globalSearchKeyword || docSearchLocalState.deepSearchKeyword)
      ) {
         return;
      }

      setGlobalSearchKeyword('');
      docSearchLocalState.deepSearchKeyword = '';

      setSearchFilters(filters);

      await handleUpdateFilterDropdown();
   }

   function clearSearchKeywords() {
      sharedClearSearchKeywords();
      docSearchLocalState.deepSearchKeyword = '';

      searchKeywordsForceClearCount.value++;
   }

   /* ------------------------------------------------------------------------------------------- */
   /*                                  SERIALIZATION & UNDO                                       */
   /* ------------------------------------------------------------------------------------------- */

   function getSerializableState(): UrlSerializableFilterState {
      const searchFilters: Record<string, SearchKeyItem[]> = {};
      for (const [field, values] of Object.entries(sharedFilterState.searchKeyword.filters)) {
         searchFilters[field] = [...values];
      }

      return {
         bureaus: [...sharedFilterState.bureaus],
         documentTypes: [...sharedFilterState.documentTypes],
         searchFilters,
         globalSearchKeyword: sharedFilterState.globalSearchKeyword,
         sortField: sharedFilterState.sortField,
         sortOrder: sharedFilterState.sortOrder,
         yearType: sharedFilterState.year.type,
         year: sharedFilterState.year.year,
         startYear: sharedFilterState.year.startYear,
         endYear: sharedFilterState.year.endYear,
      };
   }

   function applySerializedState(state: UrlSerializableFilterState, addToHistory: boolean = true): void {
      sharedFilterState.bureaus = [...(state.bureaus || [])];
      sharedFilterState.documentTypes = [...(state.documentTypes || [])];

      if (state.searchFilters && Object.keys(state.searchFilters).length > 0) {
         const filters: Record<string, SearchKeyItem[]> = {};
         for (const [field, values] of Object.entries(state.searchFilters)) {
            filters[field] = [...values];
         }
         sharedFilterState.searchKeyword.filters = filters;
      }
      else if (state.keywords && state.values && state.keywords.length > 0) {
         // Old format: migrate parallel arrays to grouped structure
         const filters: Record<string, string[]> = {};
         for (let i = 0; i < state.keywords.length; i++) {
            const field = state.keywords[i];
            const value = state.values[i];
            if (field && value) {
               if (!filters[field]) {
                  filters[field] = [];
               }
               if (!filters[field].includes(value)) {
                  filters[field].push(value);
               }
            }
         }
         sharedFilterState.searchKeyword.filters = filters;
      }
      else {
         sharedFilterState.searchKeyword.filters = {};
      }

      sharedFilterState.globalSearchKeyword = state.globalSearchKeyword;
      sharedFilterState.sortField = state.sortField;
      sharedFilterState.sortOrder = state.sortOrder;

      searchKeywordsForceClearCount.value++;

      if (addToHistory) {
         pushCurrentStateToHistory();
      }
      else {
         const currentState = getSerializableState();
         lastSerializedState = JSON.parse(JSON.stringify(currentState));

         // Persist to sessionStorage only if there are actual filters.
         const hasFilters = currentState.bureaus.length > 0
            || currentState.documentTypes.length > 0
            || Object.keys(currentState.searchFilters || {}).length > 0
            || currentState.globalSearchKeyword
            || currentState.sortField;

         if (hasFilters) {
            persistFilterState(currentState);
         }
      }
   }

   function pushCurrentStateToHistory(): void {
      const currentState = getSerializableState();

      if (lastSerializedState && areFiltersEqual(currentState, lastSerializedState)) {
         return;
      }

      undoHistory.pushState(currentState);
      lastSerializedState = JSON.parse(JSON.stringify(currentState));

      // Persist to sessionStorage only if there are actual filters.
      // This prevents empty state from overwriting saved filters.
      const hasFilters = currentState.bureaus.length > 0
         || currentState.documentTypes.length > 0
         || Object.keys(currentState.searchFilters || {}).length > 0
         || currentState.globalSearchKeyword
         || currentState.sortField;

      if (hasFilters) {
         persistFilterState(currentState);
      }
   }

   async function undoFilterChange(): Promise<boolean> {
      const previousState = undoHistory.undo();
      if (!previousState) {
         return false;
      }

      applySerializedState(previousState, false);

      localPaginationState.firstRow = 0;
      sharedPagination.currentPage = 1;
      await fetchDocuments();

      return true;
   }

   const canUndo = computed(() => undoHistory.canUndo.value);

   async function applyMetaAttributeFilter(
      fieldName: string,
      value: string,
      mode?: 'ADD' | 'ONLY' | 'REMOVE',
   ) {
      const filters = sharedFilterState.searchKeyword.filters;
      const valueExists = filters[fieldName]?.includes(value) ?? false;

      if (mode === 'REMOVE') {
         if (valueExists) {
            sharedToggleSearchFilterValue(fieldName, value, 'REMOVE');
         }
         else {
            return;
         }
      }
      else if (mode === 'ONLY') {
         setGlobalSearchKeyword('');
         docSearchLocalState.deepSearchKeyword = '';
         sharedFilterState.searchKeyword.filters = { [fieldName]: [value] };

         await handleUpdateFilterDropdown();
         return;
      }
      else if (mode === 'ADD') {
         if (!valueExists) {
            sharedToggleSearchFilterValue(fieldName, value, 'ADD');
         }
      }
      else {
         // Auto mode selection
         if (valueExists) {
            sharedToggleSearchFilterValue(fieldName, value, 'REMOVE');
         }
         else if (Object.keys(filters).length > 0) {
            sharedToggleSearchFilterValue(fieldName, value, 'ADD');
         }
         else {
            setGlobalSearchKeyword('');
            docSearchLocalState.deepSearchKeyword = '';
            sharedFilterState.searchKeyword.filters = { [fieldName]: [value] };
         }
      }

      await handleUpdateFilterDropdown();
   }

   /* ------------------------------------------------------------------------------------------- */
   /*                                      PAGINATION                                             */
   /* ------------------------------------------------------------------------------------------- */

   async function navigateToPage(page: number) {
      if (page < 1 || page > sharedPagination.totalPages) {
         return;
      }

      sharedPagination.currentPage = page;
      localPaginationState.firstRow = (page - 1) * sharedPagination.itemsPerPage;

      await fetchDocuments();
   }

   /**
    * Change the number of items displayed per page
    * Resets to page 1 and refetches documents
    * Persists the preference to localStorage
    */
   async function changePageSize(newSize: number) {
      if (newSize === sharedPagination.itemsPerPage) {
         return;
      }

      sharedPagination.itemsPerPage = newSize;
      localPaginationState.numberOfRows = newSize;

      // Persist to localStorage
      persistPageSize(newSize);

      // Reset to first page
      sharedPagination.currentPage = 1;
      localPaginationState.firstRow = 0;

      await fetchDocuments();
   }

   /* ------------------------------------------------------------------------------------------- */
   /*                                  INITIALIZATION & RESET                                     */
   /* ------------------------------------------------------------------------------------------- */

   async function initialize(force: boolean = false) {
      if (isInitialized.value && !force) {
         return;
      }

      await fetchDocuments();

      isInitialized.value = true;

      pushCurrentStateToHistory();
   }

   function softReset() {
      abortRequest();

      sharedFilterState.bureaus = [];
      sharedFilterState.documentTypes = [];
      sharedFilterState.sortField = '';
      sharedFilterState.sortOrder = '';
      clearSearchKeywords();
   }

   function $reset() {
      clearSharedFilterState();
      docSearchLocalState.deepSearchKeyword = '';

      clearLumaDocs();

      searchKeywordsForceClearCount.value = 0;

      // Clears documents, counts, error, facets and available fields, and aborts
      // anything in flight.
      resetFetchState();
      // The page shows a spinner until the next result set lands, matching the
      // state the store is constructed in.
      fetchDocumentsState.isLoading = true;

      localPaginationState.firstRow = 0;
      localPaginationState.numberOfRows = DEFAULT_ROWS_PER_PAGE;
      sharedPagination.currentPage = 1;
      sharedPagination.itemsPerPage = DEFAULT_ROWS_PER_PAGE;
      sharedPagination.totalPages = 0;

      resetPaginationState();

      availableFieldsFromResponse.value = [];
      bureausInResults.value = [];
      docTypesInResults.value = [];

      isInitialized.value = false;

      // Note: Do NOT clear persisted filter state here - $reset is for in-memory state only.
      // sessionStorage is cleared only when user explicitly clears filters via clearAllFilters().
   }

   return {
      // State - Shared (source of truth for filters and pagination)
      filterState: sharedFilterState,
      pagination: sharedPagination,
      // State - Doc-search specific
      docSearchLocalState,
      localPaginationState,
      // Other state
      searchKeywordsForceClearCount,
      fetchDocumentsState,
      sortConfig,
      isInitialized,

      // Getters - Filter
      isFilterApplied,
      isDeepSearchFilterApplied,
      isGlobalSearchApplied,
      isSearchKeywordApplied,
      staleFilters,

      isLumaDocsAdded,
      lumaDocsChatId,
      lumaDocsThreadId,

      // Getters - Metadata
      bureaus,
      relevantBureaus,
      documentTypes,
      searchFields,
      availableFieldsFromResponse,
      getFieldDisplayName,
      bureausInResults,
      docTypesInResults,
      hasNoFilterOptions,

      // Actions - Fetch
      fetchDocuments,
      fetchAllMatchingDocIds,
      initialize,

      // Actions - Setters
      setBureaus,
      setDocumentTypes,
      setSearchFilters,
      toggleSearchFilterValue,
      setGlobalSearchKeyword,
      setDeepSearchKeyword,
      addLumaDocs,
      addLumaFilterOptions,

      // Actions - Handlers
      clearAllFilters,
      handleUpdateFilterDropdown,
      handleSearchFilters,
      handleSortChange,
      handleGlobalSearchKeyword,
      setSortConfig,
      clearSearchKeywords,
      applyMetaAttributeFilter,

      // Actions - Pagination
      navigateToPage,
      changePageSize,

      // Actions - Serialization & Undo
      getSerializableState,
      applySerializedState,
      pushCurrentStateToHistory,
      undoFilterChange,
      canUndo,

      // Session persistence
      getPersistedFilterState,

      // Reset
      softReset,
      $reset,
   };
});
